import React, {
  ComponentProps,
  createContext,
  FC,
  useContext,
  useState,
} from 'react';
import {
  Language,
  Country,
  TrackingTypeParam,
  TrackingType,
  NarvarStatus,
  NarvarStatusCode,
  ExperienceHost,
  OrderType,
  OrderTracking,
  Item,
  CustomerMarketingAttributes,
  RetailerTheme,
  RetailerTranslations,
  RetailerSettings,
  FormatBlockEddQuery,
} from '../../../../__generated__/loma';
import { ThemeProvider } from '../useTheme';
import { TranslationsProvider } from '../useTranslations';
import {
  GlobalSettingsProvider,
  DEFAULT_GLOBAL_SETTINGS_CONTEXT,
} from '../useGlobalSettings';
import { OrderTrackingProvider } from '../useOrderTracking';
import { SettingsProvider } from '../useSettings';
import {
  FeatureFlagsProvider,
  type FeatureFlags,
} from '../FeatureFlagsProvider';
import {
  AnalyticsContextType,
  AnalyticsProvider,
  DEFAULT_ANALYTICS_CONTEXT,
  WebVitals,
} from '../Analytics';
import Storage, { Keyspace, StorageType } from '../Analytics/storage';
import Nested from './Nested';
import useTitanAnalytics, {
  TitanAnalyticsProvider,
  TitanAnalyticsOptions,
} from './useTitanAnalytics';
import { GlobalSettings } from '../../../../__generated__/loma';

export enum Product {
  narvarjs = 'narvarjs',
  narvarjsBuild = 'narvarjs_build',
  narvarjsTranslations = 'narvarjs_translations',
}

export enum ExperienceHostEnum {
  NARVAR = 'NARVAR',
  EMBED = 'EMBED',
}

export enum StatusCode {
  SHPD = 'SHPD',
  INTR = 'INTR',
  OFDL = 'OFDL',
  DLVD = 'DLVD',
  DLPP = 'DLPP',
  PUBC = 'PUBC',
  ITTS = 'ITTS',
  ITPP = 'ITPP',
  DLTS = 'DLTS',
  EXCP = 'EXCP',
  UNDL = 'UNDL',
  BOSS = 'BOSS',
  BOPIS = 'BOPIS',
  UNKN = 'UNKN',
}

export enum LineStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export type PageContextUnsupportedURLParams = {
  [x: string]: string | string[] | undefined;
};

export interface PageContext {
  bannerName?: string;
  bannerSegmentId?: string;
  bannerSegmentName?: string;
  cache?: boolean;
  carrier?: string;
  category?: string;
  claimPortalUrl?: string;
  customerMarketingAttributes?: CustomerMarketingAttributes | null;
  destinationCountry?: string;
  destinationZip?: string;
  deviceType?: 'mobile' | 'desktop';
  email?: string;
  embedReferrerUrl?: string;
  experienceHost: ExperienceHost;
  isFallbackView?: boolean;
  itemIds?: string[];
  itemNumber?: string;
  items?: Item[];
  lineStatus?: LineStatus;
  localeCountry?: Country;
  localeLanguage?: Language;
  mainShipmentId?: string | null;
  narvarProfileUrl?: string;
  orderApiEnabled?: boolean;
  orderDate?: string;
  orderNumbers?: string[];
  orderType?: OrderType;
  originCountry?: string;
  originZip?: string;
  phone?: string;
  previewDate?: string;
  product: Product;
  productCategory?: string;
  productSkus?: string[];
  promiseDate?: string;
  requestUrl?: string;
  retailerDcId?: string;
  retailerMoniker: string;
  secured?: boolean;
  segmentId?: string;
  segmentName?: string;
  service?: string;
  sessionId?: string;
  shipDate?: string;
  skipAnalytics?: boolean;
  token?: string;
  trackingNumbers?: string[];
  trackingStatus?: NarvarStatus;
  trackingStatusCode?: NarvarStatusCode;
  trackingType?: TrackingType;
  type?: TrackingTypeParam;
  unsupportedParams?: PageContextUnsupportedURLParams;
  versionName: string;
}

export const DEFAULT_PAGE_CONTEXT: PageContext = {
  bannerName: undefined,
  bannerSegmentId: undefined,
  bannerSegmentName: undefined,
  cache: undefined,
  carrier: undefined,
  category: undefined,
  claimPortalUrl: undefined,
  customerMarketingAttributes: undefined,
  destinationCountry: undefined,
  destinationZip: undefined,
  deviceType: undefined,
  email: undefined,
  embedReferrerUrl: undefined,
  experienceHost: 'NARVAR',
  isFallbackView: false,
  itemIds: undefined,
  itemNumber: undefined,
  items: undefined,
  lineStatus: undefined,
  localeCountry: undefined,
  localeLanguage: undefined,
  mainShipmentId: undefined,
  narvarProfileUrl: undefined,
  orderApiEnabled: undefined,
  orderDate: undefined,
  orderNumbers: undefined,
  orderType: undefined,
  originCountry: undefined,
  originZip: undefined,
  phone: undefined,
  previewDate: undefined,
  product: Product.narvarjs,
  productCategory: undefined,
  productSkus: undefined,
  promiseDate: undefined,
  requestUrl: undefined,
  retailerDcId: undefined,
  retailerMoniker: 'DEFAULT_RETAILER',
  secured: undefined,
  segmentId: undefined,
  segmentName: undefined,
  service: undefined,
  sessionId: undefined,
  shipDate: undefined,
  skipAnalytics: undefined,
  token: undefined,
  trackingNumbers: undefined,
  trackingStatus: undefined,
  trackingStatusCode: undefined,
  trackingType: undefined,
  type: undefined,
  unsupportedParams: undefined,
  versionName: 'narvar',
};

/**
 * orderData being included for mustache templating purposes, particularly
 * in custom components
 */
const getFlatOrderData: (
  itemIds: PageContext['itemIds'],
  orderTrackingData?: OrderTracking,
) => {
  items: Item[];
  itemIds: string[];
  productSkus: string[];
  customerMarketingAttributes: CustomerMarketingAttributes | null | undefined;
} = (itemIdsParameter, orderTrackingData) => {
  const items = (orderTrackingData?.orders?.[0]?.shipments || []).flatMap(
    (shipment) => shipment.items,
  );

  const { productSkus, itemIds } = items.reduce<{
    productSkus: string[];
    itemIds: string[];
  }>(
    (acc, item) => {
      if (item.productSku) acc.productSkus.push(item.productSku);
      if (item.itemId) acc.itemIds.push(item.itemId);
      return acc;
    },
    { productSkus: [], itemIds: [] },
  );

  const customerMarketingAttributes:
    | CustomerMarketingAttributes
    | undefined
    | null = orderTrackingData?.orders?.[0]?.customerMarketingAttributes;

  return {
    items,
    productSkus,
    itemIds: itemIdsParameter?.length ? itemIdsParameter : itemIds,
    customerMarketingAttributes: customerMarketingAttributes
      ? {
          billingZipSha256: customerMarketingAttributes?.billingZipSha256,
          firstNameSha256: customerMarketingAttributes?.firstNameSha256,
          lastNameSha256: customerMarketingAttributes?.lastNameSha256,
          emailSha256: customerMarketingAttributes?.emailSha256,
        }
      : undefined,
  };
};

export const formatPageContext = (
  oldState: PageContext,
  orderTrackingData: OrderTracking | undefined,
): PageContext => {
  return {
    ...oldState,
    ...getFlatOrderData(oldState.itemIds, orderTrackingData),
  };
};

const PageContextContext = createContext<{
  state: PageContext;
}>({
  state: DEFAULT_PAGE_CONTEXT,
});

export type FormattedEddData = FormatBlockEddQuery['formatEdd'] | null;

const FormattedEddContext = createContext<FormattedEddData>(null);

export interface PageProviderProps {
  analytics?: ComponentProps<typeof AnalyticsProvider>['value'];
  featureFlags: FeatureFlags;
  titanAnalyticsOptions?: TitanAnalyticsOptions;
  initialState?: PageContext;
  deviceType?: 'mobile' | 'desktop';
  orderTrackingData?: OrderTracking;
  globalSettings?: GlobalSettings;
  themeData?: RetailerTheme;
  translations?: RetailerTranslations;
  settings?: RetailerSettings;
  formattedEddData?: FormattedEddData;
}

export const PageProvider: FC<React.PropsWithChildren<PageProviderProps>> = ({
  analytics = DEFAULT_ANALYTICS_CONTEXT,
  children,
  featureFlags,
  titanAnalyticsOptions,
  initialState = DEFAULT_PAGE_CONTEXT,
  deviceType,
  orderTrackingData,
  globalSettings,
  themeData,
  translations,
  settings,
  formattedEddData,
}) => {
  const [sessionId] = useState(() => {
    const id = initialState?.sessionId;
    if (id && typeof window !== 'undefined') {
      Storage(StorageType.SESSION_STORAGE, Keyspace.REQUEST_UUID).set(
        id,
        analytics.errorCallback,
      );
    }
    return id;
  });

  const { shipments } = orderTrackingData?.orders[0] || {};
  const mainShipmentId =
    initialState?.mainShipmentId ?? orderTrackingData?.orders[0].mainShipmentId;
  const currentShipment = shipments?.find(
    ({ shipmentId }) => shipmentId === mainShipmentId,
  );

  const orderNumber: string | null | undefined =
    (initialState?.orderNumbers?.[0] as string) ??
    orderTrackingData?.orders?.[0]?.tokenPayload?.['order_number'] ??
    undefined;

  const pageContextState: PageContext = formatPageContext(
    {
      ...initialState,
      sessionId,
      deviceType,
      orderNumbers: orderNumber ? [orderNumber] : undefined,
      orderApiEnabled: orderTrackingData?.orderApiEnabled,
      mainShipmentId: currentShipment?.shipmentId,
      trackingNumbers: currentShipment?.trackingNumber
        ? [currentShipment.trackingNumber]
        : undefined,
      trackingStatus: currentShipment?.status || undefined,
      trackingStatusCode: currentShipment?.statusCode || undefined,
      carrier: currentShipment?.carrier || undefined,
      isFallbackView: currentShipment?.isFallback || false,
      orderType: currentShipment?.type || undefined,
    },
    orderTrackingData,
  );

  const analyticsState: AnalyticsContextType = {
    ...analytics,
    data: {
      tag: analytics.data.tag,
      eventSchema: analytics.data.eventSchema,
      productName: analytics.data.productName,
      pageRetailerMoniker: analytics.data.pageRetailerMoniker,
      orderNumber,
      pageOrderNumber: orderNumber,
      bannerSegmentId: initialState.bannerSegmentId,
      bannerSegmentName: initialState.bannerSegmentName,
      pageLocaleCountry: initialState.localeCountry,
      pageLocaleLanguage: initialState.localeLanguage,
      pageTrackType: initialState.trackingType,
      pageUrlLocale:
        initialState.localeLanguage &&
        initialState.localeCountry &&
        `${initialState.localeLanguage}_${initialState.localeCountry}`,
      pageVersionName: initialState.versionName,
      segmentId: initialState.segmentId,
      segmentName: initialState.segmentName,
      eventCarrierMoniker: currentShipment?.carrier,
      eventTrackingNumber: currentShipment?.trackingNumber,
      eventTrackStatus: currentShipment?.status,
      eventTrackSubStatus: currentShipment?.statusCode,
      isFallbackView: currentShipment?.isFallback,
      pageShipmentStatus: currentShipment?.status,
    },
  };
  return (
    <Nested
      /* eslint-disable react/jsx-key */
      elements={[
        <OrderTrackingProvider
          value={{
            orderTrackingData,
          }}
        />,
        <PageContextContext.Provider value={{ state: pageContextState }} />,
        <AnalyticsProvider value={analyticsState} />,
        <ThemeProvider value={{ theme: themeData ?? {} }} />,
        <TranslationsProvider value={{ translations: translations ?? {} }} />,
        <SettingsProvider value={{ settings: settings ?? {} }} />,
        <GlobalSettingsProvider
          value={{
            globalSettings: globalSettings ?? DEFAULT_GLOBAL_SETTINGS_CONTEXT,
          }}
        />,
        <FormattedEddContext.Provider value={formattedEddData ?? null} />,
        // usePageContext is now available at this point
        <TitanAnalyticsProvider value={titanAnalyticsOptions} />,
        <FeatureFlagsProvider flags={featureFlags} />,
      ]}
      /* eslint-enable react/jsx-key */
    >
      {!pageContextState.skipAnalytics && <WebVitals />}
      {children}
    </Nested>
  );
};

export const usePageContext = () => useContext(PageContextContext);
export const useFormattedEdd = () => useContext(FormattedEddContext);
export { useTitanAnalytics };
